import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUniversalSearch } from '../universal-search/useUniversalSearch';
import { SelectedDependencyWithClearHistoryProps } from '../dependencies/types';
import { DependenciesContentViewType } from '../dependencies/types';
import {
  clearDependencies,
  addConfigurationItem,
  setDependenciesConfigurationItem,
  selectParsedConfigurationItems,
} from '../../../reducers/documentation/documentationReducer';
import { selectSaltoObjectTypes } from '../../../reducers/global/parserReducer';
import { useLayouts } from '../hooks/useLayouts';
import { DocumentationPageType } from '../types';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { UI_EVENTS } from '../../../services/events';
import { telemetry } from '../../../telemetry';
import { SalesforceItem } from '../../parser/ParserTypes';
import {
  setActivePageTab,
  setDocumentationPageType,
} from '../../../reducers/documentation/documentationPageReducer';
import { useUniversalSearchContext } from '../universal-search/UniversalSearchContext';
import useParser from '../../parser/useParser';
import { SearchResponseWithConfigurationItems } from '../universal-search/types';
import { selectParsedObjects } from '../../../reducers/global/parserReducer';
import { createConfigurationItemFromParserResponseItem } from '../../parser/parserUtils';
import { LAYOUT, FIELD, OBJECT } from '@server/parser';

interface UseObjectListUniversalSearchProps {
  crmOrgId: string;
}

export const useObjectListUniversalSearch = ({ crmOrgId }: UseObjectListUniversalSearchProps) => {
  const dispatch = useDispatch();
  const sendBiEvent = useSendBiEvent();
  const { fetchLayoutData } = useLayouts();
  const saltoObjectTypes = useSelector(selectSaltoObjectTypes(crmOrgId));
  const parsedObjects = useSelector(selectParsedObjects(crmOrgId));
  const parsedConfigurationItems = useSelector(selectParsedConfigurationItems(crmOrgId));
  const [isLoadingDependency, setIsLoadingDependency] = useState(false);

  const { parseObjectOnDemand } = useParser({ crmOrgId: crmOrgId ?? '' });
  const { reset } = useUniversalSearchContext();

  const handleCloseUniversalSearch = useCallback(() => {
    reset();
    dispatch(setActivePageTab({ tabId: '' }));
    dispatch(
      setDocumentationPageType({ documentationPageType: DocumentationPageType.metadataTypes }),
    );
  }, [reset, dispatch]);

  const handleClearDependencies = useCallback(() => {
    dispatch(clearDependencies());
  }, [dispatch]);

  const handleBiEventSearchStarted = useCallback(
    (props: { searchQuery: string; searchType: string }) => {
      sendBiEvent({
        name: UI_EVENTS.documentationSearchStarted,
        props,
      });
    },
    [sendBiEvent],
  );

  const handleBiEventSearchResultsPresented = useCallback(
    (props: { searchQuery: string; numberOfResults: number }) => {
      sendBiEvent({
        name: UI_EVENTS.documentationSearchResultsPresented,
        props,
      });
    },
    [sendBiEvent],
  );

  const handleItemClick = useCallback(
    async (
      props: SelectedDependencyWithClearHistoryProps,
      searchResults?: SearchResponseWithConfigurationItems,
    ) => {
      const { parentType, dependencyType, clearHistory, contentType, name, metadataId } = props;
      let { id, objectName } = props;
      let item: SalesforceItem | undefined;

      const configurationItems = searchResults?.[
        parentType as keyof typeof searchResults
      ] as SalesforceItem[];

      switch (parentType) {
        case LAYOUT:
          const isLayoutObjectParsed = parsedObjects?.find((_item) => _item.name === objectName);
          const itemInConfigurationItemsIndex = parsedConfigurationItems?.findIndex(
            (_item) => _item.metadataId === metadataId,
          );

          if (itemInConfigurationItemsIndex !== -1) {
            item = parsedConfigurationItems?.[itemInConfigurationItemsIndex];
            break;
          }

          if (!isLayoutObjectParsed && metadataId) {
            setIsLoadingDependency(true);
            const layoutData = await fetchLayoutData(metadataId, crmOrgId);
            item = layoutData;
            setIsLoadingDependency(false);
            break;
          }

          if (isLayoutObjectParsed) {
            const itemInSearchConfigurationItemsIndex = configurationItems?.findIndex(
              (_item) => _item.metadataId === metadataId,
            );
            if (itemInSearchConfigurationItemsIndex !== -1) {
              item = configurationItems?.[itemInSearchConfigurationItemsIndex];
            }
          }
          break;

        case FIELD:
          item = configurationItems?.find((_item) => _item.metadataId === metadataId);
          break;

        case OBJECT:
          item = configurationItems?.find((_item) => _item.name === name);

          if (contentType !== DependenciesContentViewType.sourceCode) {
            if (!item) {
              const _objectTypeName = saltoObjectTypes?.find((type) => type.objectType === name);

              if (_objectTypeName) {
                id = _objectTypeName?.objectType;
                objectName = _objectTypeName?.objectType;

                const fakeObjectProperties = {
                  type: OBJECT,
                  metadataId: '',
                  name: _objectTypeName?.objectType,
                  filename: '',
                  namespace: '',
                };

                item = createConfigurationItemFromParserResponseItem({
                  objectApiName: _objectTypeName.objectType,
                  keyType: OBJECT,
                  responseItem: {
                    ...fakeObjectProperties,
                    id,
                    objectName,
                    label: _objectTypeName?.label,
                  },
                });
              }
            }

            setIsLoadingDependency(true);
            item && (await parseObjectOnDemand(item.name));
            setIsLoadingDependency(false);
          }
          break;

        default:
          item = configurationItems?.find((_item: any) => _item.metadataId === metadataId);
          break;
      }

      if (!item) {
        telemetry.captureError(`Dependency item not found metadataId: ${metadataId}`);
        return;
      }

      dispatch(
        addConfigurationItem({
          crmOrgId,
          newConfigurationItem: item,
        }),
      );

      dispatch(
        setDependenciesConfigurationItem({
          id,
          parentType,
          dependencyType: dependencyType ?? '',
          name,
          objectName,
          clearHistory,
          contentType,
        }),
      );
    },
    [
      dispatch,
      crmOrgId,
      saltoObjectTypes,
      parsedObjects,
      parsedConfigurationItems,
      fetchLayoutData,
      parseObjectOnDemand,
    ],
  );

  const universalSearchResult = useUniversalSearch({
    crmOrgId,
    onItemClick: handleItemClick,
    onClearClick: handleClearDependencies,
    onBiEventSearchStarted: handleBiEventSearchStarted,
    onBiEventSearchResultsPresented: handleBiEventSearchResultsPresented,
  });

  const onSearchKeydownWithEscHandler = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        handleCloseUniversalSearch();
      }
      universalSearchResult.onSearchKeydown(event);
    },
    [handleCloseUniversalSearch, universalSearchResult],
  );

  const openFullModeUniversalSearch = useCallback(() => {
    dispatch(
      setDocumentationPageType({
        documentationPageType: DocumentationPageType.universalSearch,
      }),
    );
  }, [dispatch]);

  return {
    ...universalSearchResult,
    isLoadingDependency,
    handleCloseUniversalSearch,
    onSearchKeydownWithEscHandler,
    openFullModeUniversalSearch,
  };
};
